import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import SEO from '@/components/SEO';

const RegulaminPage = () => {
    const { language } = useLanguage();
    const { isCartOpen, setIsCartOpen } = useCart();

    const sections = [
        {
            title: language === 'PL' ? '§ 1. Postanowienia ogólne i dane Sprzedawcy' : '§ 1. General Provisions and Seller Details',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Sklep internetowy „Włoska piwnica” dostępny pod adresem internetowym sklepu prowadzony jest przez:</p>
                    <p className="font-semibold pl-4 border-l-2 border-primary/30">
                        RAIBA SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ<br />
                        Adres siedziby: ul. Apenińska 5 / 11, 01-493 Warszawa, Polska<br />
                        NIP: 5223362903, REGON: 543961555, KRS: 0000982555 (lub odpowiedni)<br />
                        Sąd Rejestrowy: Sąd Rejonowy dla m.st. Warszawy w Warszawie, Wydział Gospodarczy KRS<br />
                        Kapitał zakładowy: 5 000,00 PLN
                    </p>
                    <p>2. Dane kontaktowe dla Klientów:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Adres e-mail: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a></li>
                        <li>Telefon: <a href="tel:+48601565760" className="text-primary hover:underline">+48 601 565 760</a></li>
                        <li>Adres do zwrotów i reklamacji: ul. Powstańców Śląskich 106 c, 01-466 Warszawa</li>
                    </ul>
                    <p>3. Niniejszy Regulamin określa zasady korzystania ze Sklepu, składania zamówień, dostarczania towarów, realizacji płatności oraz uprawnienia Klienta do odstąpienia od umowy i procedury reklamacyjne.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. The online store "Włoska piwnica" is operated by:</p>
                    <p className="font-semibold pl-4 border-l-2 border-primary/30">
                        RAIBA SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ<br />
                        Registered office address: ul. Apenińska 5 / 11, 01-493 Warsaw, Poland<br />
                        VAT ID (NIP): 5223362903, REGON: 543961555, KRS: 0000982555<br />
                        Share capital: 5,000.00 PLN
                    </p>
                    <p>2. Contact details for Customers:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Email: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a></li>
                        <li>Phone: <a href="tel:+48601565760" className="text-primary hover:underline">+48 601 565 760</a></li>
                        <li>Address for returns and complaints: ul. Powstańców Śląskich 106 c, 01-466 Warsaw, Poland</li>
                    </ul>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 2. Szczególne warunki sprzedaży napojów alkoholowych' : '§ 2. Special Conditions for the Sale of Alcoholic Beverages',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Sklep prowadzi sprzedaż napojów alkoholowych (wina) na podstawie ważnego zezwolenia na sprzedaż napojów alkoholowych przeznaczonych do spożycia poza miejscem sprzedaży, wydanego zgodnie z polską ustawą o wychowaniu w trzeźwości i przeciwdziałaniu alkoholizmowi.</p>
                    <p className="font-semibold text-amber-600 dark:text-amber-400">2. Sprzedaż napojów alkoholowych jest dozwolona wyłącznie osobom pełnoletnim (które ukończyły 18 rok życia) i trzeźwym.</p>
                    <p>3. Miejsce zawarcia umowy sprzedaży: Miejscem zawarcia umowy sprzedaży jest punkt sprzedaży stacjonarnej posiadający aktualne zezwolenie, zlokalizowany pod adresem: <strong>ul. Powstańców Śląskich 106 c, 01-466 Warszawa</strong>.</p>
                    <p>4. Składając zamówienie, Klient oświadcza, że jest osobą pełnoletnią. Sprzedawca, kurier oraz osoba wydająca towar w punkcie odbioru mają prawo i prawny obowiązek zażądać okazania dokumentu tożsamości potwierdzającego wiek Klienta. W przypadku odmowy okazania dokumentu lub stwierdzenia niepełnoletności bądź nietrzeźwości, alkohol nie zostanie wydany, a zamówienie zostanie anulowane.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. The Store sells alcoholic beverages (wine) under a valid retail alcohol license for off-premises consumption issued in accordance with Polish law.</p>
                    <p className="font-semibold text-amber-600 dark:text-amber-400">2. The sale of alcoholic beverages is permitted strictly to adults (individuals at least 18 years of age) who are sober.</p>
                    <p>3. Place of contract conclusion: The sales contract is legally concluded at the licensed physical retail outlet located at: <strong>ul. Powstańców Śląskich 106 c, 01-466 Warsaw, Poland</strong>.</p>
                    <p>4. By placing an order, the Customer declares they are an adult. The Seller, courier, or person dispensing the goods has the right and obligation to verify the Customer's age. If the customer is underage, intoxicated, or refuses to present a valid ID, the alcohol will not be delivered and the order will be cancelled.</p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 3. Składanie zamówień i zawarcie umowy' : '§ 3. Placing Orders and Contract Conclusion',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Zamówienia w sklepie internetowym można składać 24 godziny na dobę przez 7 dni w tygodniu.</p>
                    <p>2. Ceny podane w sklepie są cenami brutto (zawierają podatek VAT) i są wyrażone w złotych polskich (PLN).</p>
                    <p>3. Zawarcie umowy sprzedaży następuje w momencie zatwierdzenia zamówienia przez sklep po uprzednim prawidłowym wypełnieniu formularza zamówienia przez Klienta i dokonaniu płatności.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. Orders in the online store can be placed 24 hours a day, 7 days a week.</p>
                    <p>2. Prices listed in the store are gross prices (including VAT) in Polish Zloty (PLN).</p>
                    <p>3. The sales contract is concluded when the order is accepted and confirmed by the Store after the Customer correctly fills out the form and makes the payment.</p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 4. Dostawa i koszty transportu' : '§ 4. Delivery and Shipping Costs',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Dostawa towarów realizowana jest na terytorium Rzeczypospolitej Polskiej za pośrednictwem następujących operatorów:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>InPost Paczkomaty 24/7 (koszt: 20,00 PLN)</li>
                        <li>DHL PaczkoBOX / Punkty Partnerskie (koszt: 20,00 PLN)</li>
                        <li>Kurier InPost (koszt: 20,00 PLN)</li>
                        <li>Kurier DHL (koszt: 20,00 PLN)</li>
                        <li>Odbiór osobisty pod adresem: ul. Powstańców Śląskich 106 c, 01-466 Warszawa (koszt: 0,00 PLN)</li>
                    </ul>
                    <p>2. Szacowany czas dostawy zamówienia wynosi od 1 do 2 dni roboczych od momentu zaksięgowania wpłaty i skompletowania zamówienia.</p>
                    <p>3. Aktualne koszty dostawy są każdorazowo prezentowane Klientowi w podsumowaniu koszyka przed złożeniem zamówienia.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. Delivery of goods is carried out within the territory of the Republic of Poland via:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>InPost Paczkomaty 24/7 (cost: 20.00 PLN)</li>
                        <li>DHL PaczkoBOX / Partner Points (cost: 20.00 PLN)</li>
                        <li>InPost Courier (cost: 20.00 PLN)</li>
                        <li>DHL Courier (cost: 20.00 PLN)</li>
                        <li>Personal pickup at: ul. Powstańców Śląskich 106 c, 01-466 Warsaw, Poland (cost: 0.00 PLN)</li>
                    </ul>
                    <p>2. The estimated delivery time is 1 to 2 business days from the moment the payment is cleared and the order is prepared.</p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 5. Formy płatności' : '§ 5. Payment Methods',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Sklep oferuje następujące metody płatności:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Płatności elektroniczne, BLIK oraz szybkie przelewy online obsługiwane przez operatora płatności <strong>Polskie Płatności (Polskie ePłatności Sp. z o.o.</strong> z siedzibą w Tajęcinie).</li>
                        <li>Tradycyjny przelew bankowy na rachunek Sprzedawcy.</li>
                    </ul>
                    <p>2. Klient zobowiązany jest do dokonania płatności niezwłocznie po złożeniu zamówienia, aby mogło zostać ono przekazane do realizacji.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. The store offers the following payment methods:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Electronic payments, BLIK, and fast online transfers processed by the payment operator <strong>Polskie Płatności (Polskie ePłatności Sp. z o.o.</strong> based in Tajęcina).</li>
                        <li>Traditional bank transfer to the Seller's account.</li>
                    </ul>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 6. Prawo odstąpienia od umowy (Zwroty)' : '§ 6. Right of Withdrawal (Returns)',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Klient będący Konsumentem ma prawo odstąpić od umowy sprzedaży w terminie <strong>14 dni</strong> bez podania jakiejkolwiek przyczyny.</p>
                    <p>2. Bieg terminu do odstąpienia od umowy rozpoczyna się od dnia, w którym Konsument wszedł w posiadanie rzeczy.</p>
                    <p>3. Aby skorzystać z prawa odstąpienia od umowy, Konsument musi poinformować Sprzedawcę o swojej decyzji w drodze jednoznacznego oświadczenia (np. pismo wysłane pocztą tradycyjną lub e-mailem na adres: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>).</p>
                    <p>4. Towar należy odesłać na adres: <strong>ul. Powstańców Śląskich 106 c, 01-466 Warszawa</strong> niezwłocznie, a w każdym razie nie później niż 14 dni od dnia, w którym Konsument poinformował o odstąpieniu od umowy.</p>
                    <p>5. Sprzedawca zwraca Konsumentowi wszystkie otrzymane płatności, w tym koszty dostarczenia rzeczy (z wyjątkiem dodatkowych kosztów wynikających z wybranego przez Konsumenta sposobu dostarczenia innego niż najtańszy zwykły sposób dostarczenia oferowany przez Sklep), niezwłocznie po otrzymaniu zwracanego towaru lub dowodu jego odesłania.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. A Customer who is a Consumer has the right to withdraw from the sales contract within <strong>14 days</strong> without giving any reason.</p>
                    <p>2. The return shipping address is: <strong>ul. Powstańców Śląskich 106 c, 01-466 Warsaw, Poland</strong>.</p>
                    <p>3. To exercise the right of withdrawal, the Consumer must inform the Seller of their decision by sending an email to <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>.</p>
                </div>
            )
        },
        {
            title: language === 'PL' ? '§ 7. Reklamacje' : '§ 7. Complaints',
            content: language === 'PL' ? (
                <div className="space-y-3">
                    <p>1. Sprzedawca ma obowiązek dostarczyć towar wolny od wad fizycznych i prawnych.</p>
                    <p>2. W przypadku stwierdzenia wady towaru, Klient ma prawo do złożenia reklamacji. Reklamacje należy przesyłać pisemnie na adres punktu handlowego lub drogą elektroniczną na adres e-mail: <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>.</p>
                    <p>3. Sprzedawca rozpatrzy reklamację w terminie <strong>14 dni</strong> od dnia jej otrzymania i poinformuje Klienta o sposobie jej rozstrzygnięcia.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p>1. The Seller is obliged to deliver goods free from physical and legal defects.</p>
                    <p>2. Complaints can be sent by email to <a href="mailto:kontakt@wloska-piwnica.pl" className="text-primary hover:underline">kontakt@wloska-piwnica.pl</a>.</p>
                    <p>3. The Seller will address the complaint within <strong>14 days</strong> of receipt.</p>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-background text-foreground">
            <SEO 
                title={language === 'PL' ? "Regulamin Sklepu - Włoska Piwnica" : "Terms and Conditions - Włoska Piwnica"}
                description={language === 'PL' ? "Regulamin sklepu Włoska Piwnica. Zasady zakupów, zwrotów i reklamacji." : "Terms and conditions of Włoska Piwnica store. Rules for shopping, returns, and complaints."}
                canonicalUrl="/regulamin"
            />
            <Header onCartClick={() => setIsCartOpen(true)} />

            <main className="container mx-auto px-4 pt-32 pb-12 md:pt-40 md:pb-20 max-w-4xl">
                {/* Header */}
                <div className="mb-16">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8 group">
                        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                        {language === 'PL' ? 'Powrót' : 'Back'}
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, type: "spring" }}
                        className="text-center space-y-6"
                    >
                        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
                            <Scale className="h-10 w-10 text-primary" />
                        </div>
                        <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold tracking-tight">
                            {language === 'PL' ? 'Regulamin Sklepu' : 'Terms and Conditions'}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {language === 'PL' ? 'Obowiązuje od 27 maja 2026 r.' : 'Effective as of May 27, 2026'}
                        </p>
                    </motion.div>
                </div>

                {/* Content List */}
                <div className="space-y-12">
                    {sections.map((section, index) => (
                        <motion.section
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                            viewport={{ once: true }}
                            className="border-b border-border/50 pb-8 last:border-0"
                        >
                            <h2 className="mb-4 text-xl font-display font-semibold tracking-wide text-primary">
                                {section.title}
                            </h2>
                            <div className="text-muted-foreground leading-relaxed text-sm md:text-base">
                                {section.content}
                            </div>
                        </motion.section>
                    ))}
                </div>

            </main>

            <Footer />

            <CartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
            />
        </div>
    );
};

export default RegulaminPage;
